import { Fragment, h } from 'preact';
import parse from 'html-react-parser';

/**
 * Shared HTML-sanitisation helpers for the PDP's `dangerouslySetInnerHTML` /
 * `html-react-parser` sinks. Dependency-free (no DOMPurify) — escaping plus a
 * scheme allow-list plus a tag/attribute deny-list covers the vectors these
 * sinks are exposed to (merchant text settings, product/variant metafields).
 */

/** Escape the five HTML-significant characters for safe interpolation. */
export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Allow http(s), mailto, tel, fragment/relative paths. Reject javascript:,
// data:, vbscript:, file:, etc. The trailing `[^:]+$` clause permits relative
// URLs (no scheme); any value containing a `:` must match an allowed scheme.
// Both the leading-slash branch (`\/(?!\/)`) and the no-scheme fallback
// (`(?!\/\/)[^:]+$`) reject a protocol-relative `//evil.com`, which would
// otherwise navigate off-site in a target=_blank link.
const SAFE_URL_SCHEME = /^(?:https?:|mailto:|tel:|\/(?!\/)|#|\.\.?\/|(?!\/\/)[^:]+$)/i;
// Control characters are stripped before the scheme test so a value like
// `java\x09script:` (tab/newline-smuggled) can't slip a blocked scheme past.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x1F\x7F]+/g;
// Browsers normalize backslashes to forward slashes in a URL's authority, so
// `\\evil.com`, `/\evil.com`, and `\/evil.com` are all protocol-relative
// equivalents of `//evil.com` and would navigate off-site just the same. The
// `//`-based guards above only see forward slashes, so normalize every
// backslash to a forward slash for the scheme test (a single leading backslash
// then becomes a harmless same-origin `/path`, matching browser behavior).
const BACKSLASH = /\\/g;

/** Return the URL if it uses a safe scheme, else ''. */
export const sanitizeUrl = (url) => {
  const cleaned = String(url ?? '')
    .trim()
    .replace(CONTROL_CHARS, '');
  // Test against a backslash-normalized copy, but return the original `cleaned`
  // value so legitimate URLs are passed through byte-for-byte.
  const forTest = cleaned.replace(BACKSLASH, '/');
  return SAFE_URL_SCHEME.test(forTest) ? cleaned : '';
};

// Elements that must never come from untrusted HTML.
const BLOCKED_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'form']);
// Attributes whose value is a URL and therefore needs scheme-checking.
const URL_ATTRS = new Set(['href', 'src', 'xlink:href', 'action', 'formaction', 'poster', 'background']);

/**
 * `replace` callback for html-react-parser: drops blocked elements wholesale,
 * strips inline `on*` event-handler attributes, and scheme-checks URL
 * attributes. Mutates `domNode.attribs` in place then returns undefined so the
 * library performs its normal conversion with the cleaned attributes.
 */
export const sanitizeNode = (domNode) => {
  const name = domNode?.name?.toLowerCase?.();
  if (domNode?.type === 'script' || domNode?.type === 'style' || (name && BLOCKED_TAGS.has(name))) {
    return h(Fragment, null);
  }
  if (domNode?.attribs) {
    // Mutating the parser node's attribs in place is the intended html-react-parser
    // pattern — returning undefined then lets it convert with the cleaned attrs.
    /* eslint-disable no-param-reassign */
    Object.keys(domNode.attribs).forEach((attr) => {
      const lower = attr.toLowerCase();
      if (lower.startsWith('on')) {
        delete domNode.attribs[attr];
      } else if (URL_ATTRS.has(lower)) {
        const safe = sanitizeUrl(domNode.attribs[attr]);
        if (safe) domNode.attribs[attr] = safe;
        else delete domNode.attribs[attr];
      }
    });
    /* eslint-enable no-param-reassign */
  }
  return undefined;
};

/** Parse an HTML string into sanitised Preact nodes (safe for render). */
export const parseSanitizedHtml = (html) => parse(String(html ?? ''), { replace: sanitizeNode });
